import { useEffect, useState, useContext, createContext, useMemo } from "react";
import {
  fetchRessource, updateRessource, addRessource, deleteRessource,
  addRessourceTranslation, updateRessourceTranslation, deleteRessourceTranslation
} from '../services/identification';

const IdentificationContext = createContext();

export const useIdentification = () => {
  const context = useContext(IdentificationContext);
  if (!context) {
    throw new Error("useIdentification must be used within an IdentificationProvider");
  }
  return context;
};

export const IdentificationProvider = ({ children }) => {
  const [resources, setResources] = useState({});
  const [loading, setLoading] = useState({
    fetching: {},
    mutation: {}
  });
  const [error, setError] = useState({});
  // const lang = localStorage.getItem("i18nextLng") || "fr";

  const resourceKeys = useMemo(() => ([
    'espece',
    'race',
    'type-race',
    'type-production',
    'motif-mouvement',
    'categorie-animal',
    'type-elevage',
  ]), []);

  const fetchResourceData = async (key) => {
    setLoading(prev => ({ ...prev, fetching: { ...prev.fetching, [key]: true } }));
    setError(prev => ({ ...prev, [key]: null }));
    try {
      const data = await fetchRessource(key);
      setResources(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      setError(prev => ({ ...prev, [key]: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, fetching: { ...prev.fetching, [key]: false } }));
    }
  };

  const addResourceData = async (key, item) => {
    setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: true } }));
    setError(prev => ({ ...prev, [key]: null }));
    try {
      const data = await addRessource(key, item);
      setResources(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), data]
      }));
    } catch (err) {
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: false } }));
    }
  };

  const updateResourceData = async (key, id, item) => {
    setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: true } }));
    setError(prev => ({ ...prev, [key]: null }));
    try {
      const updated = await updateRessource(key, id, item);
      setResources(prev => ({
        ...prev,
        [key]: prev[key].map(e => e.id === id ? updated : e)
      }));
    } catch (err) {
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: false } }));
    }
  };

  const deleteResourceData = async (key, id) => {
    setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: true } }));
    setError(prev => ({ ...prev, [key]: null }));
    try {
      await deleteRessource(key, id);
      setResources(prev => ({
        ...prev,
        [key]: prev[key].filter(e => e.id !== id)
      }));
    } catch (err) {
      throw err;
    } finally {
      setLoading(prev => ({ ...prev, mutation: { ...prev.mutation, [key]: false } }));
    }
  };


  const addRessouceTranslationData = async (ressource, id, data) => {
    try {
      await addRessourceTranslation(ressource, id, data);
    } catch (error) {
      throw error;
    }
  }

  const updateRessourceTranslationData = async (ressource, id, data) => {
    try {
      await updateRessourceTranslation(ressource, id, data);
    } catch (error) {
      throw error;
    }
  }
  const deleteRessourceTranslationData = async (ressource, id) => {
    try {
      await deleteRessourceTranslation(ressource, id);
    } catch (error) {
      throw error;
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    resourceKeys.forEach(key => fetchResourceData(key));
  }, [resourceKeys]);

  const contextValue = {
    resources,
    loading,
    error,
    setError,
    fetchResourceData,
    addResourceData,
    updateResourceData,
    deleteResourceData,
    addRessouceTranslationData,
    updateRessourceTranslationData,
    deleteRessourceTranslationData
  };

  return (
    <IdentificationContext.Provider value={contextValue}>
      {children}
    </IdentificationContext.Provider>
  );
};
